import Vue from "vue"
import VueI18n from "vue-i18n"
import { sessionState, providerState } from "@/state"
import DynamicProviderKey from "@/model/provider/DynamicProviderKey"

Vue.use(VueI18n)

const defaultMessages = loadDefaultLocaleMessages()

export const i18n = new VueI18n({
    locale: import.meta.env.VUE_APP_I18N_LOCALE || "en",
    fallbackLocale: import.meta.env.VUE_APP_I18N_FALLBACK_LOCALE || "en",
    messages: defaultMessages,
})

function loadDefaultLocaleMessages() {
    const modules = import.meta.glob("../i18n/locales/*.yaml", { eager: true, import: "default" })

    const messages = {}

    for (const path in modules) {
        const locale = path.split("/").at(-1).replace(".yaml", "")
        messages[locale] = modules[path]
    }

    return messages
}

export async function loadStateLocalesAsync(lang, state) {
    if (state) {
        const modules = import.meta.glob("../i18n/locales/state/*.yaml", { import: "default" })

        for (const path in modules) {
            const locale = path.split("/").at(-1).replace(".yaml", "")

            if (locale.startsWith(state)) {
                const messages = await modules[path]()
                i18n.mergeLocaleMessage(locale, messages)
            }
        }
    }
}

// We're monkey-patching the translation functions to do custom key lookups based on group/provider

const tOrig = Vue.prototype.$t
const tcOrig = Vue.prototype.$tc
const unknown = "unknown"

Vue.prototype.$t = function (key, values) {
    const resolvedKey = resolveKey(this, key, values)
    return tOrig.call(this, resolvedKey, values)
}

Vue.prototype.$tc = function (key, choice, values) {
    const resolvedKey = resolveKey(this, key, values)
    return tcOrig.call(this, resolvedKey, choice, values)
}

const keyExistsCache = {}
const keyResolutionCache = {}

function keyExists(scope, key) {
    let cacheValue = keyExistsCache[key]

    if (cacheValue === undefined) {
        cacheValue = keyExistsCache[key] = scope.$te(key)
    }

    return cacheValue
}

function resolveKey(scope, key, values) {
    const experienceType = values?.experienceType || sessionState.experienceType
    const flowType = values?.flowType || sessionState.flowType
    const providerKey = DynamicProviderKey.extractProviderKey(values?.providerKey || sessionState.providerKey)
    const offeredProviderId = providerState.getPreAuthResponse(
        values?.providerKey || sessionState.providerKey
    )?.offeredProviderId

    const cacheKey = `${providerKey || unknown}.${offeredProviderId || unknown}.${flowType || unknown}.${experienceType || unknown}.${key}`
    const cachedResolvedKey = keyResolutionCache[cacheKey]

    if (cachedResolvedKey) {
        return cachedResolvedKey
    }

    const searchKeys = []

    if (providerKey && flowType && experienceType) {
        searchKeys.push(`${providerKey}.${flowType}.${experienceType}.${key}`)
    }

    if (providerKey && flowType) {
        searchKeys.push(`${providerKey}.${flowType}.${key}`)
    }

    if (providerKey && experienceType) {
        searchKeys.push(`${providerKey}.${experienceType}.${key}`)
    }

    if (providerKey && offeredProviderId) {
        searchKeys.push(`${providerKey}.${offeredProviderId}.${key}`)
    }

    if (providerKey) {
        searchKeys.push(`${providerKey}.${key}`)
    }

    if (flowType && experienceType) {
        searchKeys.push(`${flowType}.${experienceType}.${key}`)
    }

    if (flowType) {
        searchKeys.push(`${flowType}.${key}`)
    }

    if (experienceType) {
        searchKeys.push(`${experienceType}.${key}`)
    }

    const targetKey = searchKeys.find(resolvedKey => keyExists(scope, resolvedKey))
    keyResolutionCache[cacheKey] = targetKey ?? key
    return keyResolutionCache[cacheKey]
}
